import React, { useEffect } from "react";
import {
  Shield,
  Cookie,
  Settings,
  Database,
  Globe2,
  AlertTriangle,
  Clock,
  Mail,
  ArrowLeft,
} from "lucide-react";
import { Link } from "react-router-dom";
import { motion, useAnimation } from "framer-motion";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
};

const CookiePolicy = () => {
  const controls = useAnimation();

  useEffect(() => {
    window.scrollTo(0, 0);
    controls.start("visible");
  }, [controls]);

  const cookieSections = [
    {
      icon: <Cookie className="w-6 h-6 text-yellow-400" />,
      title: "What Are Cookies?",
      content: (
        <div className="space-y-3">
          <p className="text-slate-600 leading-relaxed">
            Cookies are small text files stored on your device when you visit a
            website. They help websites remember your actions and preferences
            (such as login, language, and display settings) over a period of
            time.
          </p>
          <p className="text-slate-600 leading-relaxed">
            This Cookie Policy explains how{" "}
            <span className="font-semibold text-slate-900">Rakfort</span> uses
            cookies and similar technologies on{" "}
            <a
              href="https://rakfort.com"
              className="text-yellow-400 hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://rakfort.com
            </a>
            .
          </p>
        </div>
      ),
    },
    {
      icon: <Shield className="w-6 h-6 text-yellow-400" />,
      title: "Why We Use Cookies",
      content: (
        <div className="space-y-3">
          <p className="text-slate-600 leading-relaxed">
            We use cookies to provide, protect, and improve our Service,
            including to:
          </p>
          <ul className="list-disc list-inside text-slate-600 space-y-1">
            <li>Remember your preferences and settings</li>
            <li>Keep you signed in and secure your account</li>
            <li>Understand how you use our website</li>
            <li>Improve performance and user experience</li>
          </ul>
        </div>
      ),
    },
    {
      icon: <Database className="w-6 h-6 text-yellow-400" />,
      title: "Types of Cookies We Use",
      content: (
        <div className="space-y-3">
          <p className="text-slate-600 leading-relaxed">
            We may use the following categories of cookies:
          </p>
          <ul className="list-disc list-inside text-slate-600 space-y-1">
            <li>
              <span className="font-semibold text-slate-900">Essential cookies</span> – Required for the website to
              function properly (e.g., authentication, security).
            </li>
            <li>
              <span className="font-semibold text-slate-900">
                Preference cookies
              </span>{" "}
              – Remember your choices such as language and display preferences.
            </li>
            <li>
              <span className="font-semibold text-slate-900">
                Analytics & performance cookies
              </span>{" "}
              – Help us understand how visitors use the website so we can
              improve it.
            </li>
            <li>
              <span className="font-semibold text-slate-900">
                Marketing or third-party cookies
              </span>{" "}
              – May be used by third-party services to deliver relevant content
              or measure campaigns.
            </li>
          </ul>
        </div>
      ),
    },
    {
      icon: <Globe2 className="w-6 h-6 text-yellow-400" />,
      title: "Third-Party Cookies",
      content: (
        <div className="space-y-3">
          <p className="text-slate-600 leading-relaxed">
            Some cookies may be placed by third-party services that appear on
            our pages, such as analytics tools, embedded content, or payment
            providers.
          </p>
          <p className="text-slate-600 leading-relaxed">
            We do not control these cookies directly. Their use is governed by
            the privacy and cookie policies of the respective third parties.
          </p>
        </div>
      ),
    },
    {
      icon: <Settings className="w-6 h-6 text-yellow-400" />,
      title: "Managing Your Cookie Preferences",
      content: (
        <div className="space-y-3">
          <p className="text-slate-600 leading-relaxed">
            You can control and manage cookies in several ways:
          </p>
          <ul className="list-disc list-inside text-slate-600 space-y-1">
            <li>
              Adjust your browser settings to block or delete cookies.
            </li>
            <li>
              Disable non-essential cookies via any cookie banner or preference
              center we provide (if available).
            </li>
            <li>
              Opt out of certain third-party analytics or marketing tools where
              supported.
            </li>
          </ul>
          <p className="text-slate-600 leading-relaxed">
            Please note that blocking some types of cookies may impact your
            experience and the functionality of the website.
          </p>
        </div>
      ),
    },
    {
      icon: <AlertTriangle className="w-6 h-6 text-yellow-400" />,
      title: "Do Not Track & Similar Technologies",
      content: (
        <div className="space-y-3">
          <p className="text-slate-600 leading-relaxed">
            Some browsers offer a &quot;Do Not Track&quot; (DNT) signal. At this
            time, our Service does not respond to DNT signals, as there is no
            consistent industry standard for compliance.
          </p>
          <p className="text-slate-600 leading-relaxed">
            We may also use similar technologies such as local storage or
            pixels that work in a way comparable to cookies.
          </p>
        </div>
      ),
    },
    {
      icon: <Clock className="w-6 h-6 text-yellow-400" />,
      title: "How Long Cookies Are Stored",
      content: (
        <div className="space-y-3">
          <p className="text-slate-600 leading-relaxed">
            Some cookies are{" "}
            <span className="font-semibold text-slate-900">session cookies</span>,
            which expire when you close your browser. Others are{" "}
            <span className="font-semibold text-slate-900">persistent cookies</span>
            , which remain on your device for a set period or until you delete
            them.
          </p>
          <p className="text-slate-600 leading-relaxed">
            The exact duration depends on the cookie&apos;s purpose and your
            browser settings.
          </p>
        </div>
      ),
    },
    {
      icon: <Shield className="w-6 h-6 text-yellow-400" />,
      title: "Relation to Our Privacy Policy",
      content: (
        <div className="space-y-3">
          <p className="text-slate-600 leading-relaxed">
            Information collected through cookies may be considered personal
            data in some jurisdictions. For details on how we process your
            personal data, please refer to our{" "}
            <a
              href="/privacy-policy"
              className="text-yellow-400 hover:underline"
            >
              Privacy Policy
            </a>
            .
          </p>
        </div>
      ),
    },
    {
      icon: <Clock className="w-6 h-6 text-yellow-400" />,
      title: "Changes to This Cookie Policy",
      content: (
        <div className="space-y-3">
          <p className="text-slate-600 leading-relaxed">
            We may update this Cookie Policy from time to time to reflect
            changes in technology, law, or our practices.
          </p>
          <p className="text-slate-600 leading-relaxed">
            Updates will be posted on this page with a revised &quot;Last
            updated&quot; date. Your continued use of the Service after changes
            become effective means you accept the updated policy.
          </p>
        </div>
      ),
    },
  ];

  return (
    <div className="bg-white text-[#2c2c2c]">
      <div className="max-w-[1140px] mx-auto px-5 pt-8 pb-2">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#005a9c] hover:text-[#004a82]">
          <ArrowLeft className="w-4 h-4" />
          Home
        </Link>
      </div>
      <section className="border-b border-[#e8e8e8] bg-[#f0f7ff] py-10 md:py-12">
        <div className="max-w-[1140px] mx-auto px-5">
          <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#737373] mb-2">Legal</p>
          <h1 className="text-2xl md:text-[1.75rem] font-semibold text-[#2c2c2c] tracking-tight">Cookie policy</h1>
          <p className="mt-3 text-[#5c5c5c] max-w-2xl text-[0.95rem]">
            What cookies are, how we use them on RakFort, and the choices you have.
          </p>
        </div>
      </section>

      {/* Cookie Policy Content */}
      <section className="relative py-12 px-5 md:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div
            className="space-y-16"
            initial="hidden"
            animate={controls}
            variants={staggerContainer}
          >
            {cookieSections.map((section, index) => (
              <motion.div
                key={index}
                className="border border-[#e8e8e8] rounded-sm p-6 md:p-8 bg-[#f0f7ff]"
                variants={fadeInUp}
              >
                <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                  <div className="p-2 bg-white border border-[#e8e8e8] rounded-sm shrink-0">
                    {React.cloneElement(section.icon, {
                      className: "w-6 h-6 text-[#005a9c]",
                    })}
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-[#2c2c2c]">{section.title}</h2>
                    <div className="mt-3 text-[0.9375rem] text-[#5c5c5c]">{section.content}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div className="mt-16 text-center border border-[#e8e8e8] rounded-sm p-8 bg-[#f0f7ff]" variants={fadeInUp}>
            <h3 className="text-lg font-semibold text-[#2c2c2c] mb-2">Questions</h3>
            <p className="text-[#5c5c5c] text-sm max-w-xl mx-auto mb-4">
              For questions about this cookie policy, contact us at:
            </p>
            <a href="mailto:info@rakfort.com" className="inline-flex items-center gap-2 text-[#005a9c] font-medium hover:text-[#004a82]">
              <Mail className="w-4 h-4" />
              info@rakfort.com
            </a>
            <p className="text-xs text-[#737373] mt-4">
              This page is for informational purposes only and does not constitute legal advice.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-5 pb-12 text-center text-sm text-[#737373]">
        <p>Last updated: November 11, 2023</p>
      </div>
    </div>
  );
};

export default CookiePolicy;
